import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Navigator } from '@/shared/navigation';
import { resolvePushRoute } from '@/shared/routing';
import { Button } from '@/shared/ui';

export interface PushToastPayload {
  title?: string;
  body?: string;
  data: Record<string, string>;
}

/**
 * 인앱 푸시 토스트 카드 — sonner `toast.custom` 으로 내부 레이아웃을 통째로 소유(디자인 SSOT).
 * 아이콘 뱃지 + 타이틀 + 본문 + "보기" 액션 + 닫기(X). 색은 heviton 토큰(`bg-popover` 등)에
 * 묶여 light/dark 를 `<Toaster/>` 테마와 함께 따라간다. `route` 없으면 "보기" 는 생략.
 */
function PushToastCard({
  id,
  title,
  body,
  route,
  navigator,
}: {
  id: string | number;
  title: string;
  body?: string;
  route: string | null;
  navigator: Navigator;
}) {
  return (
    <div className="flex w-full items-start gap-3 rounded-3xl border border-border bg-popover p-4 text-popover-foreground shadow-lg">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Bell className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{title}</p>
        {body ? <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{body}</p> : null}
        {route ? (
          <Button
            variant="link"
            onClick={() => {
              navigator.push(route);
              toast.dismiss(id);
            }}
            className="mt-2 h-auto border-0 p-0 text-sm font-semibold text-primary hover:underline"
          >
            보기
          </Button>
        ) : null}
      </div>
      <Button
        variant="ghost"
        onClick={() => toast.dismiss(id)}
        aria-label="닫기"
        className="-m-1 h-auto shrink-0 rounded-md border-0 p-1 text-muted-foreground transition-colors hover:bg-transparent hover:text-foreground"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

/**
 * 포그라운드 푸시 = **인앱 토스트** (web·native 공용 정책, 참조앱 heviton-app-frontend 이식).
 *   - native: 앱 활성 중 `onMessage` → bridge `push.received` → 본 함수
 *   - web(브라우저): 탭 활성 중 `onMessage` → 본 함수
 * "보기" 탭하면 `resolvePushRoute` 로 목적지 navigate (라우팅 SSOT=web). 목적지 없으면 액션 생략.
 * `toast`(sonner)는 모듈 싱글톤이라 트리 밖(bridge 리스너)에서도 호출 가능 — AppProvider 의
 * `<Toaster/>`가 표시. `unstyled` 로 sonner 기본 카드 스타일을 끄고 `PushToastCard` 가 전담.
 */
export function showPushToast(navigator: Navigator, { title, body, data }: PushToastPayload): void {
  const route = resolvePushRoute(data);
  toast.custom(
    (id) => (
      <PushToastCard
        id={id}
        title={title ?? '헤빛온'}
        body={body}
        route={route}
        navigator={navigator}
      />
    ),
    { unstyled: true, duration: 6000 },
  );
}
